import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDownToBracket } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { styled } from '@components-shared/src/stitches';
import { downloadScreenshot } from '@utils/screenshot';
import { Button } from '../button/Button';
import { Chip } from '../elements/chips/Chip';
import { StandardModal } from '../modals/StandardModal';
import { ASPECT_RATIOS, ScreenshotContainer } from './ScreenshotContainer';

export const DownloadScreenshot = ({
  title = 'Share on social media',
  fileName = 'sound.xyz',
  onError,
  closeModal,
  portraitHtml,
  landscapeHtml,
  squareHtml,
  onDownload: onDownloadProp,
  preventClose,
}: {
  /**
   * Shows on sticky header
   */
  title: string;
  /**
   * Title of the download file, will be appended by aspect ratio, eg title-portrait.jpg
   */
  fileName: string;
  /**
   * Function called when an error happens on download. Receives the error as argument.
   * Use this to show an error message to the user, eg through a toast.
   */
  onError: (err: unknown) => void;
  /**
   * Function called to close to modal
   */
  closeModal: () => void;
  /**
   * Items to be rendered inside the modal
   * Any html element will be converted to an image
   * Make sure css either lives on same server as html,
   * or is using crossOrigin="" to avoid CORS errors
   */
  portraitHtml: React.ReactNode;
  /*
   * Used to render image for landscape
   */
  landscapeHtml: React.ReactNode;
  /*
   * Used to render image for square
   */
  squareHtml: React.ReactNode;
  onDownload?: () => void;
  preventClose?: boolean;
}) => {
  const screenshotRef = useRef<HTMLDivElement>(null);
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<(typeof ASPECT_RATIOS)[number]>(
    ASPECT_RATIOS[0],
  );
  const aspectRatios = [ASPECT_RATIOS[0], ASPECT_RATIOS[1], ASPECT_RATIOS[2]];

  const onDownload = useStableCallback(async () => {
    onDownloadProp?.();
    try {
      await downloadScreenshot({
        filename: `${fileName}-${selectedAspectRatio.label}`,
        screenshotRef,
        options: {
          cacheBust: true,
          quality: 1,
          pixelRatio: 1,
        },
      });
    } catch (err) {
      onError(err);
    }
  });

  const renderHtmlToScreenshot = () => {
    switch (selectedAspectRatio.label) {
      case 'portrait':
        return portraitHtml;
      case 'landscape':
        return landscapeHtml;
      case 'square':
        return squareHtml;
    }
  };

  const body = (
    <ScreenshotContainer screenshotRef={screenshotRef} selectedAspectRatio={selectedAspectRatio}>
      {renderHtmlToScreenshot()}
    </ScreenshotContainer>
  );

  return (
    <>
      <StandardModal
        body={body}
        title={title}
        closeModal={closeModal}
        size="medium"
        subHeader={
          <>
            <ChipContainer>
              {aspectRatios.map(option => (
                <Chip
                  key={option.label}
                  text={option.label}
                  isSelected={selectedAspectRatio.label === option.label}
                  icon={
                    <FontAwesomeIcon
                      icon={option.icon}
                      color={selectedAspectRatio.label === option.label ? 'white' : 'black'}
                    />
                  }
                  onClick={() => {
                    setSelectedAspectRatio(option);
                  }}
                />
              ))}
            </ChipContainer>

            <SubTitle>{selectedAspectRatio.description}</SubTitle>
          </>
        }
        callToAction={
          <Button
            variant="primary"
            label="Download JPG"
            leadingIcon={<FontAwesomeIcon icon={faArrowDownToBracket} />}
            onClick={onDownload}
            size="M"
          />
        }
        preventClose={preventClose}
      />
    </>
  );
};

const ChipContainer = styled('div', {
  width: 'fit-content',
  paddingTop: 16,
  paddingBottom: 24,
  gap: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
});

const SubTitle = styled('h2', {
  base: 'M',
  paddingBottom: 8,
});
